import React from "react";
import {
  FormSection,
  FileUpload,
  InputField,
} from "../../../common/FormComponents";
import {
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Box,
  Divider,
} from "@mui/material";

type EmailTemplate = {
  selectedTemplate: string;
  headerImage: string;
  footerText: string;
  socialLinks: {
    facebook: string;
    twitter: string;
    instagram: string;
    linkedin: string;
  };
};

type EmailTemplateSectionProps = {
  template: EmailTemplate;
  onChange: ({
    field,
    value,
  }: {
    field: keyof Omit<EmailTemplate, "socialLinks">;
    value: string;
  }) => void;
  onSocialLinksChange: ({
    platform,
    value,
  }: {
    platform: keyof EmailTemplate["socialLinks"];
    value: string;
  }) => void;
  errors?: Record<string, string>;
};

// Mock templates
const templates = [
  {
    id: "standard",
    name: "Standard",
    description: "Clean, professional design with your logo at the top",
  },
  {
    id: "newsletter",
    name: "Newsletter",
    description: "Multi-column layout ideal for newsletters",
  },
  {
    id: "announcement",
    name: "Announcement",
    description: "Bold header with attention-grabbing design",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Simple, text-focused design with minimal styling",
  },
];

const EmailTemplateSection = ({
  template,
  onChange,
  onSocialLinksChange,
  errors = {},
}: EmailTemplateSectionProps) => {
  const handleChange =
    (field: keyof Omit<EmailTemplate, "socialLinks">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  const handleSocialLinkChange =
    (platform: keyof EmailTemplate["socialLinks"]) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSocialLinksChange({ platform, value: e.target.value });
    };

  const handleHeaderImageChange = (file: File | null) => {
    if (file) {
      // In a real app, this would upload the file and get a URL
      // For demo purposes, we'll create a local object URL
      const url = URL.createObjectURL(file);
      onChange({ field: "headerImage", value: url });
    } else {
      onChange({ field: "headerImage", value: "" });
    }
  };

  return (
    <FormSection
      title="Email Template"
      tooltipText="Choose and customize your email template"
    >
      <div className="mb-6">
        <Typography variant="subtitle2" gutterBottom>
          Select a Template
        </Typography>

        <RadioGroup
          aria-label="email-template"
          name="email-template"
          value={template.selectedTemplate}
          onChange={handleChange("selectedTemplate")}
        >
          <Grid container spacing={2}>
            {templates.map((temp) => (
              <Grid item xs={6} sm={3} key={temp.id}>
                <Card
                  variant="outlined"
                  className={`cursor-pointer transition-all ${
                    template.selectedTemplate === temp.id
                      ? "ring-2 ring-blue-500"
                      : "hover:shadow-md"
                  }`}
                  onClick={() =>
                    onChange({
                      field: "selectedTemplate",
                      value: temp.id,
                    })
                  }
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 180,
                  }}
                >
                  <Box
                    sx={{
                      height: 80,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#f5f5f5",
                      color: "#666",
                      fontSize: 14,
                      fontWeight: 500,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    {temp.name}
                  </Box>
                  <CardContent className="p-2 flex-grow">
                    <FormControlLabel
                      value={temp.id}
                      control={<Radio />}
                      label={
                        <div>
                          <Typography variant="caption" color="text.secondary">
                            {temp.description}
                          </Typography>
                        </div>
                      }
                      sx={{ marginRight: 0 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </div>

      <Divider className="my-6" />

      <div className="mb-6">
        <Typography variant="subtitle2" gutterBottom>
          Template Customization
        </Typography>

        <div className="mb-4">
          <FileUpload
            id="header-image"
            label="Email Header Image"
            onChange={handleHeaderImageChange}
            value={template.headerImage}
            tooltipText="Image displayed at the top of your email"
            acceptedFileTypes="image/*"
            previewSize="large"
            helpText="Recommended size: 600 x 200 pixels"
          />
        </div>

        <div className="mb-4">
          <InputField
            id="footer-text"
            name="footerText"
            label="Footer Text"
            value={template.footerText}
            onChange={handleChange("footerText")}
            tooltipText="Text displayed at the bottom of your email"
            multiline
            rows={2}
          />
        </div>
      </div>

      <div className="mb-6">
        <Typography variant="subtitle2" gutterBottom>
          Social Media Links
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Add your social media links to display icons in the email footer
        </Typography>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <InputField
              id="facebook-link"
              name="facebook"
              label="Facebook URL"
              value={template.socialLinks.facebook}
              onChange={handleSocialLinkChange("facebook")}
              placeholder="https://facebook.com/yourchamber"
            />
          </div>
          <div>
            <InputField
              id="twitter-link"
              name="twitter"
              label="X (Twitter) URL"
              value={template.socialLinks.twitter}
              onChange={handleSocialLinkChange("twitter")}
              placeholder="https://x.com/yourchamber"
            />
          </div>
          <div>
            <InputField
              id="instagram-link"
              name="instagram"
              label="Instagram URL"
              value={template.socialLinks.instagram}
              onChange={handleSocialLinkChange("instagram")}
              placeholder="https://instagram.com/yourchamber"
            />
          </div>
          <div>
            <InputField
              id="linkedin-link"
              name="linkedin"
              label="LinkedIn URL"
              value={template.socialLinks.linkedin}
              onChange={handleSocialLinkChange("linkedin")}
              placeholder="https://linkedin.com/company/yourchamber"
            />
          </div>
        </div>
      </div>
    </FormSection>
  );
};

export default EmailTemplateSection;
